import { createContext, useContext } from "react";

/**
 * The market the page is shown for, from where the visitor is: the US and Australia get
 * their own; everyone else gets the UK (the default market).
 *
 * WHERE THE COUNTRY COMES FROM. The live site resolves it in the browser after load
 * (`getUserCountry()` → `/places/getCountryData`, which reads CloudFront's
 * `cloudfront-viewer-country` header) and keeps it a day in the `userCountry` cookie.
 * This page reads the same two sources ON THE SERVER instead — the header on the request
 * itself, else that cookie — so the page arrives already in the visitor's market, with
 * no extra round trip and no UK-then-US flash. Only when the server has neither (no
 * CloudFront in front, first visit) does the browser look it up, as the live site does.
 *
 * China is always GB and does not look the country up. `.com` is not in the Redis HOME
 * cache. Do not add the country to that key.
 *
 * The value is the ISO code the shared rails already read from `localStorage.country`, so
 * one code drives both the shared rails and what this page's own components change per
 * market: the hero photo, the trust strip's universities and the universities rail.
 */
export type Market = "GB" | "US" | "AU";

export const DEFAULT_MARKET: Market = "GB";

export const marketFromCountry = (country?: string | null): Market =>
  country === "US" || country === "AU" ? country : DEFAULT_MARKET;

const firstValue = (value: unknown): string | null => {
  const v = Array.isArray(value) ? value[0] : value;
  return typeof v === "string" && v.trim() ? v.trim().toUpperCase() : null;
};

/**
 * Server only: the visitor's ISO country from the request — CloudFront's header, else the
 * `userCountry` cookie a previous visit saved. Null when neither is there.
 */
export const countryFromRequest = (req: any): string | null =>
  firstValue(req?.headers?.["cloudfront-viewer-country"]) || firstValue(req?.cookies?.userCountry);

export const MarketContext = createContext<Market>(DEFAULT_MARKET);

export const useMarket = (): Market => useContext(MarketContext);
